/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Box,
  CardActions,
  Switch,
  useTheme,
  alpha,
} from '@mui/material';
import { useParams, useHistory } from 'react-router';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'src/common/Loader';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';
import {
  getSiteDetails,
  updateSiteDetails as updateSiteStatus,
} from 'src/redux/siteSlice';
import { CONFIRMATION_MSG } from 'src/utils/constants';
import useAuth from 'src/context/AuthContext';
import ROUTE from 'src/utils/routeConstants';
import {
  getFormattedDateTime12Hour,
  hasAdminLikeAccess
} from 'src/utils/getInitials';
import shouldDisplayDetailField from 'src/utils/detailFieldUtils';
import Newsite from 'src/components/sites/NewSite';

/* -------------------- Helpers -------------------- */

const formatLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const formatValue = (value) => {
  if (value === null || value === undefined) return '--';

  if (typeof value === 'boolean') return value ? 'ON' : 'OFF';

  if (typeof value === 'string' && value.includes('T')) {
    const formattedDateTime = getFormattedDateTime12Hour(value);
    if (formattedDateTime) return formattedDateTime;
  }

  return value;
};

/* -------------------- Component -------------------- */

function SiteDetails() {
  const theme = useTheme();
  const { siteId } = useParams();
  const history = useHistory();
  const { getUser } = useAuth();
  const hasAdminAccess = hasAdminLikeAccess(getUser);

  const dispatch = useDispatch();
  const { loading, siteDetails } = useSelector((state) => state.sites);

  const [openModal, setOpenModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(getSiteDetails(siteId));
  }, [dispatch, siteId]);

  const updateStatus = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);
    try {
      const values = {
        ...siteDetails,
        active: !siteDetails.active,
      };
      await dispatch(updateSiteStatus(values)).unwrap();
      dispatch(
        setSuccessInfo({ successMsg: 'Site status updated successfully!' })
      );
    } catch (error) {
      dispatch(setErrorInfo({ errorMsg: error.error_message }));
    }
    setIsLoading(false);
  };

  if (loading || isLoading) return <Loader />;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Newsite
        open={openModal}
        initialValues={siteDetails}
        handleClose={() => setOpenModal(false)}
      />

      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 24px 0 rgba(0,0,0,0.05)',
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 140px)',
        }}
      >
        {/* HEADER */}
        <CardHeader
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'sticky',
            top: 0,
            zIndex: 3,
            bgcolor: 'background.paper',
          }}
          title={(
            <Box>
              <Typography variant="h6" fontWeight={700}>
                SITE DETAILS
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and manage site configuration
              </Typography>
            </Box>
          )}
          action={(
            <Button
              variant="outlined"
              startIcon={<ArrowBackIosNewIcon />}
              onClick={() => history.replace(ROUTE.sites)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                color: 'text.secondary',
                borderColor: 'divider',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                },
              }}
            >
              Back
            </Button>
          )}
        />

        {/* CONTENT */}
        <CardContent sx={{ p: 4, flex: 1, overflowY: 'auto' }}>
          <Grid container spacing={3}>
            {Object.entries(siteDetails || {}).map(([key, value]) => {
              if (!shouldDisplayDetailField(key)) {
                return null;
              }

              if (key === 'active') {
                return (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                        },
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                          display="block"
                        >
                          STATUS
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={
                            value ? 'success.main' : 'error.main'
                          }
                        >
                          {value ? 'Active' : 'Inactive'}
                        </Typography>
                      </Box>

                      <Switch
                        checked={value}
                        onChange={() => hasAdminAccess && setShowConfirmModal(true)}
                        disabled={!hasAdminAccess}
                        color="success"
                      />
                    </Box>
                  </Grid>
                );
              }

              return (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      height: '100%',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                      },
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 0.5,
                      }}
                    >
                      {formatLabel(key)}
                    </Typography>

                    <Typography variant="body1" fontWeight={600}>
                      {formatValue(value)}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>

        {/* ADMIN ACTION */}
        {hasAdminAccess && (
          <>
            <Divider />
            <CardActions
              sx={{
                p: 2.5, justifyContent: 'flex-end', position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider'
              }}
            >
              <Button
                startIcon={<EditIcon />}
                variant="contained"
                onClick={() => setOpenModal(true)}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Edit Site Details
              </Button>
            </CardActions>
          </>
        )}
      </Card>

      <ConfirmationDialog
        info={
          siteDetails.active
            ? CONFIRMATION_MSG.disableSite
            : CONFIRMATION_MSG.enableSite
        }
        open={showConfirmModal}
        disable={siteDetails.active}
        onClose={() => setShowConfirmModal(false)}
        onSubmit={updateStatus}
      />
    </Container>
  );
}

export default SiteDetails;
